// ─────────────────────────────────────────────────────────────────────────────
// margin_wipe.ts — Animated content removal for reserved page zones
//
// When the stylus sidebar (or any future utility) claims a region of
// the page, the user's existing content inside that region doesn't
// just disappear: it gets *wiped* with a procedural animation that
// makes the menu feel like it cleared its own space. The data on the
// reserved area is genuinely lost — undo aside, the page mutates —
// but the visual eviction is the show.
//
// Two animations ship today:
//
//   `fade`        — the clipped fragment fades to transparent in
//                   place over ~700 ms.
//   `slice-fall`  — the fragment "snaps off" at the polygon edge and
//                   falls under gravity with a small wobble,
//                   dimming as it goes (~1.1 s).
//
// Adding a new animation = new entry in `runFragmentAnim`, plus a
// kind in WipeAnim. The clipper and dispatch are independent.
//
// Geometry: only axis-aligned vertical-line clipping for now (the
// sidebar is a vertical strip). `clipPolylineAtVerticalLine` returns
// the inside / outside runs along the polyline; the canvas dispatches
// mutations from `outside` (rebuilding the surviving stroke) and
// pushes `inside` runs as animated fragments.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint, InkStyle } from '@shared/notebook_model';
import type { InkLayer } from './ink_layer';

export type WipeAnim = 'fade' | 'slice-fall';

export interface WipeFragment {
  /** Sub-stroke that originally lived inside the reserved area, with
   *  endpoints clipped to the boundary so the fragment lines up
   *  visually with the edge it broke off from. */
  points: StrokePoint[];
  /** InkStyle snapshot of the parent stroke — gives the fragment
   *  the same brush feel during the animation. */
  style: InkStyle;
  /** Which animation runs. The wipe layer picks per-fragment so a
   *  multi-segment clip can mix fade and slice-fall for variety. */
  anim: WipeAnim;
  /** performance.now() at spawn. */
  birth: number;
  /** Cached centre-x of the fragment, used by slice-fall as the
   *  rotation anchor so the piece pivots around its own middle. */
  cx: number;
  cy: number;
}

const FADE_DUR_MS  = 700;
const FALL_DUR_MS  = 1100;
const FALL_GRAVITY = 600;        // px per second² in fall animation

/** Clip a polyline at a vertical line x = thresholdX. Returns
 *  contiguous runs of points that lie on each side of the line, with
 *  crossing segments split exactly at thresholdX (interpolated point
 *  carries lerped pressure / tilt / timestamp).
 *
 *  `inside` = runs with x < thresholdX (the reserved column).
 *  `outside` = runs with x ≥ thresholdX (the drawable area).
 *
 *  Each returned run preserves the original direction of travel, so
 *  the inside fragments visually start where they originally entered
 *  the column. Single-point inputs degenerate to the side they sit
 *  on. */
export function clipPolylineAtVerticalLine(
  points: readonly StrokePoint[],
  thresholdX: number,
): { inside: StrokePoint[][]; outside: StrokePoint[][] } {
  const inside: StrokePoint[][] = [];
  const outside: StrokePoint[][] = [];
  if (points.length === 0) return { inside, outside };
  if (points.length === 1) {
    if (points[0].x < thresholdX) inside.push([points[0]]);
    else outside.push([points[0]]);
    return { inside, outside };
  }

  let curIn:  StrokePoint[] = [];
  let curOut: StrokePoint[] = [];
  const isIn = (p: StrokePoint) => p.x < thresholdX;

  if (isIn(points[0])) curIn.push(points[0]);
  else curOut.push(points[0]);

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const aIn = isIn(a);
    const bIn = isIn(b);
    if (aIn === bIn) {
      (bIn ? curIn : curOut).push(b);
      continue;
    }
    // Crossing — interpolate exactly at thresholdX.
    const t = (thresholdX - a.x) / (b.x - a.x);
    const cross = lerpStrokePoint(a, b, t);
    if (aIn) {
      curIn.push(cross);
      if (curIn.length > 0) inside.push(curIn);
      curIn = [];
      curOut = [cross, b];
    } else {
      curOut.push(cross);
      if (curOut.length > 0) outside.push(curOut);
      curOut = [];
      curIn = [cross, b];
    }
  }
  if (curIn.length > 0)  inside.push(curIn);
  if (curOut.length > 0) outside.push(curOut);
  return { inside, outside };
}

/** Clip a polyline at a circle. Like `clipPolylineAtVerticalLine` but
 *  with a disc as the clip region — used by the eraser to subtract
 *  exactly the swept area instead of nuking whole strokes. Only handles
 *  endpoint-driven crossings; segments that pass entirely through the
 *  circle without an inside endpoint are left untouched (subsequent
 *  eraser samples land closer and catch any missed pieces). */
export function clipPolylineAtCircle(
  points: readonly StrokePoint[],
  cx: number, cy: number, r: number,
): { inside: StrokePoint[][]; outside: StrokePoint[][] } {
  const inside: StrokePoint[][] = [];
  const outside: StrokePoint[][] = [];
  if (points.length === 0) return { inside, outside };
  const r2 = r * r;
  const isIn = (p: { x: number; y: number }): boolean => {
    const dx = p.x - cx, dy = p.y - cy;
    return dx * dx + dy * dy <= r2;
  };

  if (points.length === 1) {
    if (isIn(points[0])) inside.push([points[0]]);
    else outside.push([points[0]]);
    return { inside, outside };
  }

  let curIn:  StrokePoint[] = [];
  let curOut: StrokePoint[] = [];
  if (isIn(points[0])) curIn.push(points[0]);
  else curOut.push(points[0]);

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const aIn = isIn(a);
    const bIn = isIn(b);
    if (aIn === bIn) {
      (bIn ? curIn : curOut).push(b);
      continue;
    }
    // Solve |A + t·(B-A) - C|² = r² for t ∈ [0,1].
    const dx = a.x - cx, dy = a.y - cy;
    const Dx = b.x - a.x, Dy = b.y - a.y;
    const A = Dx * Dx + Dy * Dy;
    if (A < 1e-9) { (bIn ? curIn : curOut).push(b); continue; }
    const B = 2 * (dx * Dx + dy * Dy);
    const C = dx * dx + dy * dy - r2;
    const disc = B * B - 4 * A * C;
    if (disc < 0) { (bIn ? curIn : curOut).push(b); continue; }
    const sq = Math.sqrt(disc);
    const t1 = (-B - sq) / (2 * A);
    const t2 = (-B + sq) / (2 * A);
    let t = (t1 >= 0 && t1 <= 1) ? t1 : t2;
    t = Math.max(0, Math.min(1, t));
    const cross = lerpStrokePoint(a, b, t);

    if (aIn) {
      curIn.push(cross);
      if (curIn.length > 0) inside.push(curIn);
      curIn = [];
      curOut = [cross, b];
    } else {
      curOut.push(cross);
      if (curOut.length > 0) outside.push(curOut);
      curOut = [];
      curIn = [cross, b];
    }
  }
  if (curIn.length > 0)  inside.push(curIn);
  if (curOut.length > 0) outside.push(curOut);
  return { inside, outside };
}

/** Soften the surviving outside runs of a circle clip by ramping
 *  point pressure from 0 (at rHard) to 1 (at rOuter). The eraser's
 *  opacity setting drives the gap between rHard and rOuter — full
 *  opacity collapses the gap (hard cut, no smoothing); lower opacity
 *  widens the smooth zone so the stroke ends fade out gradually
 *  rather than terminating in a clean circle.
 *
 *  Modifies point pressure only; geometry stays put. Returns fresh
 *  StrokePoint objects so the caller can dispatch them as new ink
 *  commits without disturbing the originals. */
export function softenOutsideRuns(
  outside: readonly StrokePoint[][],
  cx: number, cy: number,
  rHard: number, rOuter: number,
): StrokePoint[][] {
  if (rOuter - rHard < 1e-3) {
    // No smooth zone — return verbatim copies.
    return outside.map(run => run.map(p => ({ ...p })));
  }
  const denom = rOuter - rHard;
  return outside.map(run => run.map(p => {
    const d = Math.hypot(p.x - cx, p.y - cy);
    if (d >= rOuter) return { ...p };
    const factor = Math.max(0, Math.min(1, (d - rHard) / denom));
    return { ...p, pressure: p.pressure * factor };
  }));
}

function lerpStrokePoint(a: StrokePoint, b: StrokePoint, t: number): StrokePoint {
  return {
    x:         a.x + (b.x - a.x) * t,
    y:         a.y + (b.y - a.y) * t,
    pressure:  a.pressure + (b.pressure - a.pressure) * t,
    tiltX:     a.tiltX + (b.tiltX - a.tiltX) * t,
    tiltY:     a.tiltY + (b.tiltY - a.tiltY) * t,
    twist:     a.twist + (b.twist - a.twist) * t,
    timestamp: a.timestamp + (b.timestamp - a.timestamp) * t,
  };
}

/** Pick a random animation kind. Future iterations can take page
 *  context (current section, time of day, …) and weight the choice. */
export function pickWipeAnim(): WipeAnim {
  return Math.random() < 0.5 ? 'fade' : 'slice-fall';
}

/** Live fragments + per-frame draw. Pure renderer — owns no clipping
 *  logic; the canvas builds fragments and pushes them in. */
export class MarginWipeLayer {
  private _fragments: WipeFragment[] = [];

  push(frag: WipeFragment): void {
    this._fragments.push(frag);
  }

  clear(): void { this._fragments = []; }

  /** Whether any fragment is still alive (useful for telling the
   *  render loop to keep ticking). */
  get isActive(): boolean { return this._fragments.length > 0; }

  /** Per-frame draw. The caller should set up a page-local ctx
   *  translate (ctx.translate(L.x, L.y)) BEFORE calling — fragments
   *  carry page-local coordinates. */
  draw(
    ctx: CanvasRenderingContext2D,
    ink: InkLayer,
    nowMs: number,
  ): void {
    for (let i = this._fragments.length - 1; i >= 0; i--) {
      const f = this._fragments[i];
      const dur = f.anim === 'fade' ? FADE_DUR_MS : FALL_DUR_MS;
      const t = (nowMs - f.birth) / dur;
      if (t >= 1) {
        this._fragments.splice(i, 1);
        continue;
      }
      runFragmentAnim(ctx, ink, f, t);
    }
  }
}

/** Dispatch table for each animation kind. New animations slot in
 *  here as additional cases. */
function runFragmentAnim(
  ctx: CanvasRenderingContext2D,
  ink: InkLayer,
  f: WipeFragment,
  t: number,                  // 0..1 progress
): void {
  switch (f.anim) {
    case 'fade':       drawFade(ctx, ink, f, t); break;
    case 'slice-fall': drawSliceFall(ctx, ink, f, t); break;
  }
}

// ── Fade animation ──────────────────────────────────────────────────────────
//
// Simple alpha ramp 1 → 0 over the fragment's duration. Easy on
// the eye; reads as "the menu absorbed this".

function drawFade(
  ctx: CanvasRenderingContext2D,
  ink: InkLayer,
  f: WipeFragment,
  t: number,
): void {
  const alpha = 1 - t;
  if (alpha <= 0.02) return;
  const fadedStyle: InkStyle = { ...f.style, opacity: f.style.opacity * alpha };
  ink.renderShapeStroke(ctx, f.points, fadedStyle);
}

// ── Slice-and-fall animation ────────────────────────────────────────────────
//
// The fragment's points stay frozen in their original positions; the
// transform handles the motion. Translate downward under gravity,
// rotate slightly around the fragment's own centre for personality,
// and fade in the tail end so the piece doesn't pile up at the page
// bottom.

function drawSliceFall(
  ctx: CanvasRenderingContext2D,
  ink: InkLayer,
  f: WipeFragment,
  t: number,
): void {
  // Half-second of free-fall via classical gravity, in pixels.
  const dt = (FALL_DUR_MS / 1000) * t;
  const fallY = 0.5 * FALL_GRAVITY * dt * dt;
  const wobble = Math.sin(t * 7) * 0.06 * t;
  const alpha  = Math.max(0, 1 - t * 1.15);

  ctx.save();
  // Pivot around the fragment's centre so rotation looks anchored.
  ctx.translate(f.cx, f.cy);
  ctx.rotate(wobble);
  ctx.translate(-f.cx, -f.cy + fallY);
  const fadedStyle: InkStyle = { ...f.style, opacity: f.style.opacity * alpha };
  ink.renderShapeStroke(ctx, f.points, fadedStyle);
  ctx.restore();
}

/** Convenience: build a ready-to-push fragment from a clipped
 *  inside-run. Computes the centre point used by slice-fall. */
export function makeFragment(
  points: StrokePoint[],
  style: InkStyle,
  anim: WipeAnim,
  birth: number,
): WipeFragment {
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  cx /= points.length;
  cy /= points.length;
  return { points, style, anim, birth, cx, cy };
}
